(async () => {
	const debugMode = 1,
		testId = "GLX-042",
		logInfo = testId + " -->",
		variation = 1;

	try {
		// Avoid code to run multiple times
		if (!document.documentElement.classList.contains(`${testId}_var_${variation}`)) {
			document.documentElement.classList.add(`${testId}_var_${variation}`);
			document.documentElement.classList.add(`${testId}_var`);

			// Tampermonkey has a specific window scope
			let _window = window;
			if (typeof unsafeWindow !== "undefined") _window = unsafeWindow;

			_window._conv_q = _window._conv_q || [];

			// Helper function to wait for elements
			const waitForElement = (selector) => {
				return new Promise((resolve) => {
					if (document.querySelector(selector)) {
						return resolve(document.querySelector(selector));
					}

					const observer = new MutationObserver(() => {
						if (document.querySelector(selector)) {
							observer.disconnect();
							resolve(document.querySelector(selector));
						}
					});

					observer.observe(document.documentElement, {
						childList: true,
						subtree: true
					});
				});
			};

			// Create quick add overlay HTML
			const createQuickAddOverlay = (handle) => {
				return `
					<div class="${testId}_gray_overlay"></div>
        			<div class="${testId}_quick_add_overlay">
						<button class="${testId}_close_button">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
								<path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
								<path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
						</button>
						<div class="${testId}_product_options">
							<div class="${testId}_variant_selectors"></div>
							<div class="${testId}_quantity">
								<label>Quantity</label>
								<input type="number" value="1" min="1">
							</div>
							<button class="${testId}_add_to_cart">Add to Cart</button>
						</div>
					</div>
				`;
			};

			const fetchProductData = async (handle) => {
				try {
					// Read options from the product JSON rather than splitting JSON-LD variant names.
					// Those names can't be split reliably (titles like "EARTH JACKET - TREES", values like
					// "SA2 / EU35") and don't say which option is which, so the labels had to be guessed as
					// ["Color", "Size"], which swapped them on Size-first products.
					// The quick-buy-modal handle attribute carries "?variant=<id>", which must not precede ".js"
					const response = await fetch(`/products/${handle.split("?")[0]}.js`);
					if (!response.ok) return null;
					const product = await response.json();

					const hasOnlyDefaultVariant = product.variants.length === 1 && product.variants[0].title === "Default Title";

					const variants = product.variants.map((variant) => ({
						id: variant.id,
						title: variant.title,
						options: hasOnlyDefaultVariant ? [] : variant.options,
						price: variant.price,
						available: variant.available
					}));

					// Option names in the product's own order, e.g. ["Size", "Colour"]
					const optionTypes = hasOnlyDefaultVariant ? [] : product.options.map((option) => (typeof option === "string" ? option : option.name));

					return {
						variants,
						optionTypes
					};
				} catch (err) {
					console.error("Error fetching product data:", err);
					return null;
				}
			};

			function updateVariantUI(selectedOptions, productData, container) {
				// Clear existing variant selectors
				container.innerHTML = "";

				const { variants, optionTypes } = productData;

				// Create a selector for each option type
				optionTypes.forEach((optionType, index) => {
					const selectorContainer = document.createElement("div");
					selectorContainer.classList.add("variant-selector");

					const label = document.createElement("label");
					label.textContent = optionType;
					selectorContainer.appendChild(label);

					const select = document.createElement("select");
					// Keyed by position, not name: option names can contain spaces ("Accessory size")
					select.classList.add(`${testId}_option${index}_selector`);

					// Get unique values for this option type
					const values = [...new Set(variants.map((v) => v.options[index]))];

					values.forEach((value) => {
						const option = document.createElement("option");
						option.value = value;
						option.textContent = value;

						// Check if this option combination is available
						const isAvailable = variants.some((variant) => {
							const matchesSelected = selectedOptions.every((selected, i) => (i === index ? value === variant.options[i] : selected === variant.options[i]));
							return matchesSelected && variant.available;
						});

						if (!isAvailable) {
							option.disabled = true;
							option.textContent = `${value} - Sold out`;
						}

						if (value === selectedOptions[index]) {
							option.selected = true;
						}

						select.appendChild(option);
					});

					select.addEventListener("change", updateVariantState);
					selectorContainer.appendChild(select);
					container.appendChild(selectorContainer);
				});

				// Update initial state
				updateVariantState();

				function updateVariantState() {
					const currentOptions = optionTypes.map((type, index) => container.querySelector(`select.${testId}_option${index}_selector`)?.value);

					// Update availability of options in each selector
					optionTypes.forEach((optionType, optionIndex) => {
						const currentSelect = container.querySelector(`select.${testId}_option${optionIndex}_selector`);

						Array.from(currentSelect.options).forEach((option) => {
							// Skip empty options
							if (!option.value) return;

							// Store original text if not already stored
							if (!option.getAttribute("data-original-text")) {
								// Remove "- Sold out" if it exists in the original text
								const cleanText = option.text.replace(" - Sold out", "");
								option.setAttribute("data-original-text", cleanText);
							}

							// Create temporary options array to check availability
							const tempOptions = [...currentOptions];
							tempOptions[optionIndex] = option.value;

							// Check if this option combination exists and is available
							const isAvailable = variants.some((variant) => {
								const matchesOptions = variant.options.every((opt, idx) => opt === tempOptions[idx]);
								return matchesOptions && variant.available;
							});

							// Update option state
							option.disabled = !isAvailable;
							const originalText = option.getAttribute("data-original-text");
							option.text = isAvailable ? originalText : `${originalText} - Sold out`;
						});
					});

					const selectedVariant = variants.find((v) => v.options.length === selectedOptions.length && v.options.every((opt, i) => opt === currentOptions[i]));

					const form = container.closest("form");
					if (form) {
						const addToCartButton = form.querySelector(`.${testId}_add_to_cart`);
						const variantIdInput = form.querySelector('input[name="id"]');

						if (selectedVariant) {
							if (variantIdInput) {
								variantIdInput.value = selectedVariant.id;
							}

							if (addToCartButton) {
								addToCartButton.disabled = !selectedVariant.available;
								addToCartButton.textContent = selectedVariant.available ? "Add to cart" : "Sold out";
							}
						} else {
							// If no variant matches the selection, disable the button
							if (addToCartButton) {
								addToCartButton.disabled = true;
								addToCartButton.textContent = "Unavailable";
							}
						}
					}
				}
			}

			function updateVariantAvailability(variantData) {
				const colorSelect = document.querySelector('[data-option-name="Color"]');
				const sizeSelect = document.querySelector('[data-option-name="Size"]');

				if (!colorSelect || !sizeSelect) return;

				// Get all variants from the JSON-LD data
				const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
				const jsonLd = JSON.parse(jsonLdScript.textContent);
				const variants = jsonLd.hasVariant;

				// Create a map of out of stock variants
				const outOfStockVariants = new Map();
				variants.forEach((variant) => {
					const [color, size] = variant.name.split(" - ")[1].split(" / ");
					if (variant.offers.availability === "http://schema.org/OutOfStock") {
						outOfStockVariants.set(`${color}-${size}`, true);
					}
				});

				// Update option availability
				const updateOptionAvailability = (select) => {
					Array.from(select.options).forEach((option) => {
						if (option.value === "") return; // Skip placeholder option

						const otherSelect = select === colorSelect ? sizeSelect : colorSelect;
						const otherValue = otherSelect.value;

						if (otherValue) {
							// Check if this specific combination is out of stock
							const key = select === colorSelect ? `${option.value}-${otherValue}` : `${otherValue}-${option.value}`;

							const isOutOfStock = outOfStockVariants.has(key);
							option.disabled = isOutOfStock;

							// Update text to show sold out status
							const baseText = option.getAttribute("data-original-text") || option.text;
							if (!option.getAttribute("data-original-text")) {
								option.setAttribute("data-original-text", baseText);
							}
							option.text = isOutOfStock ? `${baseText} - Sold out` : baseText;
						} else {
							// If other select has no value, check if this option is out of stock in all combinations
							const isFullyOutOfStock = Array.from(otherSelect.options)
								.filter((opt) => opt.value !== "") // Skip placeholder
								.every((opt) => {
									const key = select === colorSelect ? `${option.value}-${opt.value}` : `${opt.value}-${option.value}`;
									return outOfStockVariants.has(key);
								});

							option.disabled = isFullyOutOfStock;

							// Update text for fully out of stock options
							const baseText = option.getAttribute("data-original-text") || option.text;
							if (!option.getAttribute("data-original-text")) {
								option.setAttribute("data-original-text", baseText);
							}
							option.text = isFullyOutOfStock ? `${baseText} - Sold out` : baseText;
						}
					});
				};

				// Update both selects
				updateOptionAvailability(colorSelect);
				updateOptionAvailability(sizeSelect);

				// Call this function whenever a select changes
				colorSelect.addEventListener("change", () => {
					updateOptionAvailability(colorSelect);
					updateOptionAvailability(sizeSelect);
				});

				sizeSelect.addEventListener("change", () => {
					updateOptionAvailability(colorSelect);
					updateOptionAvailability(sizeSelect);
				});
			}

			const handleAddToCart = async (e, productCard, productData) => {
				e.preventDefault();
				e.stopPropagation();

				try {
					// Get selected options
					const selectedOptions = [];
					productData.optionTypes.forEach((type, index) => {
						const value = document.querySelector(`select.${testId}_option${index}_selector`)?.value;
						if (value) selectedOptions.push(value);
					});

					const quantity = document.querySelector(`.${testId}_quantity input`).value;

					// Find matching variant
					const variant = productData.variants.find((v) => v.options.length === selectedOptions.length && v.options.every((opt, idx) => opt === selectedOptions[idx]));

					if (!variant) {
						console.error("No matching variant found");
						return;
					}

					const formData = {
						items: [
							{
								id: variant.id,
								quantity: parseInt(quantity, 10)
							}
						]
					};

					const response = await fetch("/cart/add.js", {
						method: "POST",
						headers: {
							"Content-Type": "application/json"
						},
						body: JSON.stringify(formData)
					});

					if (!response.ok) throw new Error("Add to cart failed");

					// Success handling
					productCard.classList.remove(`${testId}_active`);
					document.querySelector(`.${testId}_quick_add_overlay`)?.remove();
					document.querySelector(`.${testId}_gray_overlay`)?.remove();

					// Fetch updated cart HTML
					const cartResponse = await fetch("/?section_id=cart-drawer");
					const cartHTML = await cartResponse.text();

					// Update cart drawer if it exists
					const cartDrawer = document.querySelector("cart-drawer, #cart-drawer, .cart-drawer");
					if (cartDrawer) {
						cartDrawer.innerHTML = new DOMParser().parseFromString(cartHTML, "text/html").querySelector("cart-drawer, #cart-drawer, .cart-drawer").innerHTML;
					}

					// Open the mini cart by clicking the header cart button
					const cartToggle = document.querySelector('header a[aria-controls="cart-drawer"]');
					if (cartToggle) {
						cartToggle.click();
					}
				} catch (error) {
					console.error("Error adding to cart:", error);
				}
			};

			// Handle quick add button click
			const handleQuickAdd = async (e) => {
				e.preventDefault();
				e.stopPropagation();

				const existingQuickAddOverlay = document.querySelector(`.${testId}_quick_add_overlay`);
				const existingGrayOverlay = document.querySelector(`.${testId}_gray_overlay`);
				if (existingQuickAddOverlay) existingQuickAddOverlay.remove();
				if (existingGrayOverlay) existingGrayOverlay.remove();

				const productCard = e.currentTarget.closest(".product-card");
				const quickBuyModal = e.currentTarget.nextElementSibling;
				const handle = quickBuyModal?.getAttribute("handle");

				// If no handle, this is a simple product without variants
				if (!handle) {
					const form = productCard.querySelector("form");
					if (form) {
						const formData = new FormData(form);
						try {
							const response = await fetch("/cart/add.js", {
								method: "POST",
								body: formData
							});

							if (!response.ok) throw new Error("Add to cart failed");

							// Update cart and open drawer
							const cartResponse = await fetch("/?section_id=cart-drawer");
							const cartHTML = await cartResponse.text();

							const cartDrawer = document.querySelector("cart-drawer, #cart-drawer, .cart-drawer");
							if (cartDrawer) {
								cartDrawer.innerHTML = new DOMParser().parseFromString(cartHTML, "text/html").querySelector("cart-drawer, #cart-drawer, .cart-drawer").innerHTML;
							}

							const cartToggle = document.querySelector('header a[aria-controls="cart-drawer"]');
							if (cartToggle) {
								cartToggle.click();
							}
						} catch (error) {
							console.error("Error adding to cart:", error);
						}
						return;
					}
				}

				// Continue with existing variant selection logic for products with variants
				// On mobile the overlay is a fixed bottom-sheet, so render it on <body> to escape the
				// product-card's own stacking context (theme.css gives .product-card isolation: isolate),
				// otherwise the drawer is trapped behind page content regardless of its z-index.
				// On desktop it stays inside the card so the absolute-positioned popover anchors to the card.
				const overlayHost = window.matchMedia("(min-width: 750px)").matches ? productCard : document.body;
				overlayHost.insertAdjacentHTML("beforeend", createQuickAddOverlay(handle));
				productCard.classList.add(`${testId}_active`);

				const variantSelectorsContainer = document.querySelector(`.${testId}_variant_selectors`);

				// Get the selected color from the product tile (add this before productData fetch)
				const selectedColorInput = productCard.querySelector('input[name^="swatch--"]:checked');
				const selectedColorLabel = selectedColorInput ? selectedColorInput.nextElementSibling.querySelector(".sr-only").textContent : null;

				const productData = await fetchProductData(handle);
				if (productData && productData.variants.length > 0) {
					// Find variant that matches the selected color
					let initialVariant;
					if (selectedColorLabel) {
						initialVariant = productData.variants.find((v) => v.available && v.options.some((opt) => opt.toLowerCase() === selectedColorLabel.toLowerCase()));
					}

					// Fallback to first available variant if no match found
					if (!initialVariant) {
						initialVariant = productData.variants.find((v) => v.available) || productData.variants[0];
					}

					const initialOptions = initialVariant.options;

					updateVariantUI(initialOptions, productData, variantSelectorsContainer);

					const addToCartButton = document.querySelector(`.${testId}_add_to_cart`);
					addToCartButton.addEventListener("click", (e) => handleAddToCart(e, productCard, productData));
				}

				// Handle close button click
				const closeButton = document.querySelector(`.${testId}_close_button`);
				const closeOverlays = () => {
					productCard.classList.remove(`${testId}_active`);
					document.querySelector(`.${testId}_quick_add_overlay`)?.remove();
					document.querySelector(`.${testId}_gray_overlay`)?.remove();
				};

				closeButton.addEventListener("click", (e) => {
					e.stopPropagation();
					closeOverlays();
				});

				// Handle gray overlay click
				const grayOverlay = document.querySelector(`.${testId}_gray_overlay`);
				grayOverlay.addEventListener("click", (e) => {
					e.stopPropagation();
					closeOverlays();
				});

				// Handle click outside
				const handleClickOutside = (event) => {
					if (!event.target.closest(`.${testId}_product_options`) && !event.target.closest(".product-card__quick-add-button")) {
						closeOverlays();
						document.removeEventListener("click", handleClickOutside);
					}
				};

				document.addEventListener("click", handleClickOutside);
			};

			// Initialise
			waitForElement("body").then(() => {
				try {
					new MutationObserver(() => {
						document.querySelectorAll(`button.product-card__quick-add-button:not(.${testId}_event_added)`).forEach((button) => {
							button.classList.add(`${testId}_event_added`);
							button.addEventListener("click", handleQuickAdd);
						});
					}).observe(document.body, {
						childList: true,
						subtree: true
					});

					document.querySelectorAll(`button.product-card__quick-add-button:not(.${testId}_event_added)`).forEach((button) => {
						button.classList.add(`${testId}_event_added`);
						button.addEventListener("click", handleQuickAdd);
					});
				} catch (err) {
					if (debugMode) console.error(logInfo, err.message);
				}
			});
		}
	} catch (err) {
		if (debugMode) console.error(logInfo, err.message);
	}
})();